import { FC, useLayoutEffect, useRef, useState } from 'react'
import { AlertCircle, Info } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import type { PatientSaveWarning } from '../../contexts/ChatContext'
import { describeSaveError } from '../../utils/describeSaveError'

const SAVE_FAILED = 'Could not save patient data. Please try again.'

/**
 * What to say when the request was never answered.
 *
 * SAVE_FAILED asserts a fact — "your text was not stored" — that nobody on this
 * side of the wire knows. Extraction runs 60-120s against a 180s axios timeout,
 * and the backend commits the clinical write BEFORE it serialises a response,
 * so a timeout, a dropped connection or a gateway giving up all leave the save
 * genuinely undetermined and most often already committed. Telling the
 * clinician it failed and inviting a retry is therefore wrong twice over: it
 * denies a stored edit, and on the create branch the retry writes a second row
 * and re-runs extraction. So this names the real state and sends them to check
 * rather than to resubmit.
 */
const SAVE_OUTCOME_UNKNOWN =
  'The server did not answer in time. Your edit may or may not have been saved — ' +
  'reload the patient panel to check before submitting it again.'

/**
 * A save refusal whose own message is meant for the clinician.
 *
 * `errorDetail` replaces every other rejection with generic retry advice, which
 * is right for transport faults but wrong for a structural refusal — retrying
 * cannot create an active workspace. Throwing this marks a message as
 * deliberately user-facing, without letting an incidental TypeError's text
 * reach a clinical surface.
 *
 * A real Error subclass rather than the tagged plain object it used to be: it
 * is thrown, and handleSave's `console.error('… save failed', err)` is the only
 * trace this surface leaves. A plain object arrives there with no stack, which
 * is precisely the case — a structural refusal that will recur — where the
 * trace is worth having. The `userFacingSaveError` discriminant is kept because
 * the narrowing reads it structurally, so the guard still holds for a value
 * that crossed a realm boundary and lost its prototype.
 */
export class PatientDataSaveError extends Error {
  readonly userFacingSaveError = true as const

  constructor(message: string) {
    super(message)
    this.name = 'PatientDataSaveError'
  }
}

const isUserFacingSaveError = (err: unknown): err is PatientDataSaveError =>
  typeof err === 'object' &&
  err !== null &&
  (err as Partial<PatientDataSaveError>).userFacingSaveError === true &&
  typeof (err as Partial<PatientDataSaveError>).message === 'string'

/**
 * A request that got no answer from the application itself.
 *
 * Deliberately narrow. `!err.response` alone would also swallow a TypeError
 * thrown inside handleSave, which IS a definite failure and must keep the
 * definite wording — so this only fires on shapes axios produces when it made a
 * request and got nothing back (`request` set, `response` unset), or on its
 * timeout/abort codes. 502 and 504 count too: those come from a proxy saying
 * the upstream never answered IT, which leaves the write just as undetermined.
 * 503 is excluded on purpose — this backend owns its 503 and states the outcome
 * in `detail`, which the caller above has already preferred.
 *
 * `isAxiosError` alone is deliberately NOT enough. Axios stamps it on every
 * error it owns, including the ones it raises before a request is ever sent —
 * a bad baseURL, an interceptor throwing, an unresolvable adapter. Those never
 * reached the server, so "may or may not have been saved" would be this
 * surface's own false statement. The shapes that DID reach the wire always
 * carry one of the arms below: axios attaches `request` for network failures
 * and sets `code` for timeouts and aborts.
 */
const isOutcomeUnknown = (err: unknown): boolean => {
  if (typeof err !== 'object' || err === null) return false
  const e = err as {
    request?: unknown
    code?: unknown
    response?: { status?: unknown }
  }
  const status = e.response?.status
  if (typeof status === 'number') return status === 502 || status === 504
  if (e.response) return false
  return !!e.request || e.code === 'ECONNABORTED' || e.code === 'ETIMEDOUT'
}

/**
 * What to say when the save was refused for going too fast.
 *
 * /parse is rate-limited (10/minute) and slowapi answers with `{"error": ...}`,
 * NOT `{"detail": ...}` — so a 429 fell all the way through to SAVE_FAILED's
 * "Please try again", which is the one instruction guaranteed to reproduce it.
 * The refusal is deterministic and the fix is a pause, so say that.
 */
const SAVE_RATE_LIMITED =
  'Too many saves in a short time, so this one was not stored. Wait a minute and save again.'

/**
 * Pydantic stamps this on the front of every message a field/model validator
 * raises via ValueError. Whatever follows it is the actual complaint; the prefix
 * is FastAPI plumbing and means nothing to the reader.
 */
const PYDANTIC_VALUE_ERROR_PREFIX = 'Value error, '

/**
 * FastAPI's 422 shape: `detail` is an ARRAY of error objects, not a string.
 *
 * Every other refusal on this route hands back `detail` as a string, so the
 * string check below was the whole reader — and a 422 rendered as the generic
 * "Please try again" for a refusal that is deterministic and will refuse the
 * identical retry forever. What still arrives this way is any request-model
 * rejection: `text`'s min_length, a field of the wrong type.
 *
 * The ambiguous-scope refusal used to be the headline case and no longer is —
 * it is a plain 400 now, because FastAPI's 422 body echoes each error's `input`
 * and for a model-level validator that input is the whole request body, i.e.
 * the pasted chart text (see main.py's _reject_ambiguous_scope, and #559 for the
 * app-wide gap). Joins the `msg` strings so a body carrying more than one error
 * does not silently show only the first.
 */
const arrayDetailMessage = (detail: unknown): string | null => {
  if (!Array.isArray(detail)) return null
  const messages = detail
    .map((entry) => (entry as { msg?: unknown })?.msg)
    .filter((msg): msg is string => typeof msg === 'string' && msg.trim().length > 0)
    .map((msg) =>
      msg.startsWith(PYDANTIC_VALUE_ERROR_PREFIX)
        ? msg.slice(PYDANTIC_VALUE_ERROR_PREFIX.length)
        : msg
    )
  return messages.length > 0 ? messages.join(' ') : null
}

/** Prefer the backend's `detail`, then a deliberately user-facing message. */
const errorDetail = (err: unknown): string => {
  const response = (err as { response?: { status?: unknown; data?: { detail?: unknown } } })
    ?.response
  const detail = response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) return detail
  // Before the 429 arm below only because a rate-limit body that DID carry a
  // usable message should still win over the generic wording.
  const fromArray = arrayDetailMessage(detail)
  if (fromArray) return fromArray
  if (response?.status === 429) return SAVE_RATE_LIMITED
  if (isUserFacingSaveError(err) && err.message.trim()) return err.message
  if (isOutcomeUnknown(err)) return SAVE_OUTCOME_UNKNOWN
  return SAVE_FAILED
}

/**
 * What a committed-but-incomplete save tells the clinician. ChatContext reports
 * which promises the save could not keep; the wording lives here, with the
 * surface that shows it, and always names the consequence and the next step.
 */
const WARNING_COPY: Record<PatientSaveWarning, string> = {
  'casey-not-notified':
    'Casey was not told about this change. Mention it in chat — otherwise Casey keeps reasoning from the panel as it was before your edit.',
  'panel-not-refreshed':
    'The patient panel could not reload, so it still shows the previous values. Reload the page to see your edit.',
  'audit-not-recorded':
    'Your edit is saved, but the internal access record for it did not write. Nothing further is needed from you — let your administrator know if you see this repeatedly.',
  'save-unconfirmed':
    'The server accepted the edit but sent nothing back, so none of it could be confirmed. Reload the page to see what was stored before editing again.',
}

interface PatientDataInputDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Persists the entered chart text. Must reject when the save fails, and
   * resolve with any promises a committed save could not keep.
   */
  onSave: (text: string) => Promise<PatientSaveWarning[]>
}

/**
 * Manual patient-data entry (#444).
 *
 * The patient panel's "Edit patient data" menu item opens this. File upload
 * stays the primary intake path; this is the type-or-paste alternative for
 * chart text that never arrives as a file.
 *
 * Saving re-runs backend extraction over the pasted text, stores the result
 * and notifies Casey — but it is NOT a panel rebuild, and the copy below no
 * longer says it is. `upsert_patient_clinical_data_by_workspace` writes
 * patient_summary, allergies, code_status and extractions, while
 * `fromExtractions` resolves every section as
 * `structured ?? panel_content ?? extractions`. On a post-intake workspace the
 * first two are populated, so pasted vitals/labs/imaging/orders/medications
 * lose to what intake wrote and the section the clinician was correcting does
 * not move. Promising a re-extract there sends them off to re-paste text that
 * was stored correctly the first time.
 */
export const PatientDataInputDialog: FC<PatientDataInputDialogProps> = ({
  open,
  onOpenChange,
  onSave,
}) => {
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Promises a COMMITTED save could not keep. Non-empty means the edit is
  // stored but something the dialog advertised did not happen, which is a
  // third state: not a failure to retry, not a success to close over.
  const [warnings, setWarnings] = useState<readonly PatientSaveWarning[]>([])
  // This dialog's OWN request, not any patient-data work happening elsewhere.
  // The shared ChatContext loading flag is also set by the conversation-change
  // load, so wiring the button to it disabled Save and said "Saving..." for a
  // request the clinician never made.
  const [isSaving, setIsSaving] = useState(false)
  // Which attempt the dialog is currently showing. handleSave captures this at
  // submit time and re-checks it before writing any state back, so a save still
  // in flight from a PREVIOUS opening cannot paint its outcome into the edit
  // now on screen.
  const saveGeneration = useRef(0)
  // Whether an onSave promise is STILL UNRESOLVED, across closes and reopens.
  //
  // saveGeneration decides whose outcome may be painted; it cannot stop a
  // second request from being sent. The dialog stays mounted across a close, so
  // close -> reopen -> Save fired a SECOND concurrent POST for the same
  // workspace while the first was still running. Both requests upsert the same
  // row last-write-wins, extraction takes 60-120s, and nothing orders their
  // completions — so the attempt the clinician ABANDONED can commit after the
  // one they meant to keep, and the panel ends up showing the abandoned text.
  // One attempt at a time is the only ordering this surface can guarantee.
  //
  // A ref, not state: it must survive the reset effect below, and nothing
  // renders from it directly (isSaving already carries the button's wording).
  const isSaveInFlight = useRef(false)

  // Reset the form each time the dialog opens so a previous attempt's text,
  // error or warnings never leak into the next edit.
  //
  // useLayoutEffect, not useEffect: a passive effect runs AFTER the browser
  // paints, so a reopened dialog showed one frame of the previous attempt — the
  // last patient's chart text under a "Saved" banner — before clearing. One
  // frame is enough to read as "this is still here", and on a clinical surface
  // the thing being flashed is the previous patient's data. The layout effect
  // runs after the DOM is built and before the paint, so the first frame the
  // clinician ever sees is already clean. (jsdom does not paint, so no test can
  // observe the difference; the guarantee is the hook choice itself.)
  useLayoutEffect(() => {
    if (open) {
      setText('')
      setError(null)
      setWarnings([])
      // WorkspaceManager renders this dialog unconditionally (only Radix's
      // DialogContent unmounts), so the component and its state survive a
      // close. Left stale, a save the clinician cancelled mid-flight — the
      // 60-120s extraction makes that ordinary — reopened the dialog with Save
      // already disabled and reading "Saving...", so the retry they came back
      // to make could not be submitted at all.
      //
      // Conditional, because clearing it unconditionally bought that retry by
      // letting it race: the reopened dialog could submit while the previous
      // request was still in the air. So the button unlocks when the previous
      // attempt has SETTLED — the ordinary cancel-then-retry case — and stays
      // disabled and reading "Saving..." while it has not. handleSave's
      // `finally` clears the ref and the flag together the moment it lands, so
      // the wait is exactly as long as the request itself.
      if (!isSaveInFlight.current) setIsSaving(false)
      // Everything still in flight belongs to the opening that just ended.
      saveGeneration.current += 1
    }
  }, [open])

  const trimmed = text.trim()
  // 'save-unconfirmed' is NOT a committed save. ChatContext raises it when the
  // response carried no body at all, which is the one outcome where the write
  // cannot be asserted — so folding it in with the committed-but-degraded
  // warnings put "none of it could be confirmed" under a heading reading
  // "Saved", locked the textarea, and replaced Save with Close. That left the
  // clinician told to go and check, and given no way to act on what they found.
  // It is a THIRD state: unknown, and still the clinician's to resolve.
  const isUnconfirmed = warnings.includes('save-unconfirmed')
  const isSaved = warnings.length > 0 && !isUnconfirmed

  const handleSave = async () => {
    // isSaveInFlight, not just isSaving: the reset effect above may have
    // cleared isSaving for an attempt that is still running (a close and
    // reopen), and the ref is what actually knows.
    if (!trimmed || isSaving || isSaved || isSaveInFlight.current) return
    setError(null)
    // A retry from the unconfirmed state must not keep the previous attempt's
    // banner on screen while the new one runs.
    setWarnings([])
    setIsSaving(true)
    isSaveInFlight.current = true
    // Captured before the await: everything below is checked against it.
    const attempt = saveGeneration.current
    try {
      const saveWarnings = await onSave(trimmed)
      // Superseded — the dialog was closed and reopened while this was in
      // flight. The write DID commit, but this outcome describes text that is
      // no longer on screen: closing would shut the reopened dialog out from
      // under the clinician, and flipping it to "Saved — but not everything
      // followed through" reads as "my new edit was saved" when it was not.
      // The still-open dialog reports on its OWN attempt or nothing.
      if (attempt !== saveGeneration.current) return
      // A save whose follow-through failed must not close as an unambiguous
      // success. The write IS committed, so re-submitting would duplicate it —
      // hold the dialog open and say what did not happen instead.
      if (saveWarnings.length > 0) setWarnings(saveWarnings)
      else onOpenChange(false)
    } catch (err) {
      // The three known rejection sources log upstream, but anything thrown
      // inside handleSave itself collapses into generic retry advice with no
      // trace from this surface — and that advice is wrong for a fault that
      // will fail identically every time. Logged even when superseded: a failed
      // request is worth a trace whether or not its dialog is still open.
      console.error('[PatientDataInputDialog] save failed', describeSaveError(err))
      if (attempt !== saveGeneration.current) return
      setError(errorDetail(err))
    } finally {
      isSaveInFlight.current = false
      // Unconditional, unlike the generation-guarded writes above. The guard
      // used to exist so a stale attempt could not unlock a NEWER save that was
      // genuinely still running — but the ref now admits one attempt at a time,
      // so no such newer save can exist. Keeping the guard here would instead
      // strand a reopened dialog on a permanently disabled "Saving..." button:
      // the superseded attempt is the only thing left that can clear it.
      setIsSaving(false)
    }
  }

  /**
   * Dismissal, refused while a save is in flight.
   *
   * Cancel closed the dialog immediately, but the request it left behind is the
   * one that COMMITS — the write lands 60-120s later either way. Its resolution
   * then reports the promises that committed save could NOT keep
   * ('casey-not-notified', 'audit-not-recorded') into a dialog nobody is
   * looking at, the generation guard drops them, and the reset effect wipes
   * whatever survived on the next open. The edit is stored, Casey never heard
   * about it, and no surface ever says so — which is the exact state
   * notice_delivered exists to make visible. Holding the dialog until the
   * request settles is the only way those warnings reach the person who needs
   * them, and the wait is exactly as long as the save itself.
   *
   * Radix routes Escape, an outside pointer-down and the corner close button
   * through onOpenChange too, so guarding it here covers every dismissal this
   * dialog offers rather than the Cancel button alone. A parent that drives
   * `open` to false still wins: that is its decision, not a dismissal.
   */
  const handleDismissRequest = (next: boolean) => {
    if (!next && isSaving) return
    onOpenChange(next)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault()
      handleSave()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleDismissRequest}>
      <DialogContent className="sm:max-w-[640px]" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle>Edit patient data</DialogTitle>
          <DialogDescription>
            Paste or type chart text. Saving updates the patient summary and what Casey works
            from; the panel's vitals, labs, imaging, orders and medications keep the values intake
            produced. Uploading files stays the fastest way to bring in a chart.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <label htmlFor="patient-data-text" className="text-sm font-medium">
            Chart text
          </label>
          <Textarea
            id="patient-data-text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Paste the chart text to extract into the patient panel"
            rows={12}
            className="resize-none text-sm font-mono"
            autoFocus
            disabled={isSaved || isSaving}
          />
        </div>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md bg-destructive/10 border border-destructive/20 px-3 py-2"
          >
            <AlertCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {warnings.length > 0 && (
          <div
            role="status"
            className={
              isUnconfirmed
                ? 'flex items-start gap-2 rounded-md bg-muted border border-border px-3 py-2'
                : 'flex items-start gap-2 rounded-md bg-amber-500/10 border border-amber-500/30 px-3 py-2'
            }
          >
            <Info
              className={`h-4 w-4 mt-0.5 shrink-0 ${
                isUnconfirmed ? 'text-muted-foreground' : 'text-amber-600'
              }`}
            />
            <div className="space-y-1">
              <p className="text-sm font-medium">
                {isUnconfirmed
                  ? 'Could not confirm whether this was saved'
                  : 'Saved — but not everything followed through'}
              </p>
              {warnings.map((warning) => (
                <p key={warning} className="text-sm text-muted-foreground">
                  {WARNING_COPY[warning]}
                </p>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {isSaved ? (
            // The edit is stored, so "Cancel" would read as undo and "Save"
            // would invite a duplicate write. Acknowledging is the only
            // honest action left.
            <Button type="button" onClick={() => handleDismissRequest(false)}>
              Close
            </Button>
          ) : (
            <>
              {isSaving && (
                // Says why Cancel is unavailable. A disabled button with no
                // explanation reads as a hung dialog on a surface where the
                // wait is a minute or two of real extraction.
                <p className="mr-auto self-center text-sm text-muted-foreground">
                  Saving... please wait
                </p>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => handleDismissRequest(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="button" onClick={handleSave} disabled={!trimmed || isSaving}>
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
